import React from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import Skeleton from "connectui/component/display/Skeleton";
import Text from "connectui/component/display/Text";
import ICONS from "connectui/icons/iconMap";
import SvgIcon from "connectui/component/display/SvgIcon";

const templateTypeIcons = {
  bp: ICONS["boarding-pass-ticket"],
  ai: ICONS.ai,
  experience: ICONS["message-circle"],
  other: ICONS["message-circle"],
};

const useStyles = (theme) =>
  buildStyles({
    menuItem: {
      flex: 1,
      padding: theme.spacing(2.5, 3.5),
      cursor: "pointer",
      overflow: "hidden",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.colors.gray100,
      },
    },
    menuIcon: {
      marginRight: theme.spacing(2),
      display: "flex",
      alignItems: "center",
      width: "20px",
      height: "20px",
    },
    text: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },
    textContainer: {
      flex: 1,
      overflow: "hidden",
    },
  });

const TemplatesMenu = ({
  open,
  menuRef,
  searchText,
  data,
  selectedIndex,
  skeleton = {enabled: false},
  anchorEl,
  onClose,
  onSelect,
  offset = 0,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  const highlightText = (text, index, textVariant, selected) => {
    if (index === -1) return text;
    const length = searchText.length;
    return (
      <>
        {text.substring(0, index)}
        <Text
          as="span"
          variant={textVariant}
          weight="medium"
          color={selected ? "white" : "textPrimary"}
          sx={{
            borderRadius: theme.spacing(1),
            backgroundColor: selected
              ? theme.colors.warning400
              : theme.colors.warning300,
          }}
        >
          {text.substring(index, index + length)}
        </Text>
        {text.substring(index + length)}
      </>
    );
  };

  const menuItems = skeleton.enabled
    ? [
        {
          Component: () => <Skeleton width={"200px"} height={"52px"} />,
        },
        {
          Component: () => <Skeleton width={"200px"} height={"52px"} />,
        },
        {
          Component: () => <Skeleton width={"200px"} height={"52px"} />,
        },
      ]
    : data.map((templ, i) => ({
        Component: ({onClick, selected}) => (
          <FlexContainer
            alignItems="center"
            className={classes.menuItem}
            onClick={onClick}
            sx={selected ? {backgroundColor: theme.colors.primary} : undefined}
          >
            <SvgIcon
              component={templateTypeIcons[templ.type]}
              className={classes.menuIcon}
              sx={{
                color: selected
                  ? theme.colors.white
                  : theme.colors.textSecondary,
              }}
            />
            <FlexContainer
              flexDirection="column"
              className={classes.textContainer}
            >
              <Text
                as="div"
                weight="medium"
                color={selected ? "white" : "textPrimary"}
                className={classes.text}
              >
                {highlightText(
                  templ.title,
                  templ.titleIndex,
                  "textMD",
                  selected,
                )}
              </Text>
              <Text
                as="div"
                variant="textXS"
                color={selected ? "white" : "textSecondary"}
                className={classes.text}
              >
                {highlightText(
                  templ.text,
                  templ.subheaderIndex,
                  "textXS",
                  selected,
                )}
              </Text>
            </FlexContainer>
          </FlexContainer>
        ),
        selected: selectedIndex === i,
        onClick: () => onSelect(templ),
      }));

  return (
    <ContextMenu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      listProps={{listRef: menuRef}}
      sx={{
        maxHeight: "300px",
        maxWidth: anchorEl ? anchorEl.offsetWidth - offset * 2 : "100%",
        overflow: "hidden auto",
        marginLeft: offset,
      }}
      modalProps={{
        anchorOrigin: {vertical: "top", horizontal: "start"},
        modalPosition: {vertical: "bottom", horizontal: "start"},
      }}
      menuItems={menuItems}
    />
  );
};

export default TemplatesMenu;
